import * as angular from "angular";
import { Feature } from "../models/feature.model";
import { FeatureService } from "../services/feature.service";
import { featuresConstants } from "../utils/constants/features.constants";
import {
    PermissionLevel,
    permissionLevelsConstants,
} from "../utils/constants/permission-levels.constants";

export class PermissionCheck {
    public signatureFeaturePermission: any;
    public readonly features = [...featuresConstants.allFeatures];
    public readonly canvasserNotAllowedFeatures = [
        ...featuresConstants.canvasserNotAllowedFeatures,
    ];
    public levels: PermissionLevel[] = permissionLevelsConstants.levels.map(
        (level: PermissionLevel) => ({ ...level })
    );

    public permissionid: string | null = null;
    public featurePermissions = null;
    public hasStripeSubscription: boolean = false;
    public isYearlySubscrition: boolean = false;
    public consentSettings = null;
    public securitySettings = null;
    public featureCalled: boolean = false;
    public resultFeaturePermission: boolean = false;
    public billingRight: boolean = false;
    public allFeaturesPromise: any;

    private $cookies: any;
    private $timeout: any;
    private $q: any;
    private $http: any;
    private __env: any;
    private $filter: any;

    private featureService: FeatureService;

    constructor(
        FeatureService: FeatureService,
        $cookies: any,
        $timeout: any,
        $q: any,
        $http: any,
        __env: any,
        $filter: any
    ) {
        this.$cookies = $cookies;
        this.$timeout = $timeout;
        this.$q = $q;
        this.$http = $http;
        this.__env = __env;
        this.$filter = $filter;
        this.featureService = FeatureService;
    }

    clearAll() {
        this.permissionid = null;
        this.featurePermissions = null;
        this.resultFeaturePermission = false;
        this.consentSettings = null;
        this.securitySettings = null;
        this.featureCalled = false;
        this.allFeaturesPromise = null;
        this.billingRight = false;
    }

    setPermission(permissionid: string) {
        this.permissionid = permissionid;
    }

    setBillingRight(billingRight: boolean) {
        this.billingRight = billingRight;
    }

    hasBillingRight(): boolean {
        return this.billingRight;
    }

    setHasStripeSubscription(hasStripeSubscription: boolean) {
        this.hasStripeSubscription = hasStripeSubscription;
    }

    setIsYearlySubscrition(isYearlySubscrition: boolean) {
        this.isYearlySubscrition = isYearlySubscrition;
    }

    getConsentSettings(): Promise<any> {
        const deferred = this.$q.defer();
        this.getFeaturePermissions().then(() =>
            deferred.resolve(this.consentSettings)
        );
        return deferred.promise;
    }

    getSecuritySettings(): Promise<any> {
        const deferred = this.$q.defer();
        this.getFeaturePermissions().then(() =>
            deferred.resolve(this.securitySettings)
        );
        return deferred.promise;
    }

    getFeaturePermissions(): Promise<any> {
        const deferred = this.$q.defer();
        const consentNames = [
            "SIGNATURE",
            "CAMPAIGNUPDATES",
            "NEWSLETTER",
            "VOLUNTEERUPDATES",
            "EMAIL_SUMMARY",
            "CONSENT",
        ];
        const security = ["2FA"];

        if (this.featurePermissions !== null) {
            return this.$q.resolve(this.featurePermissions);
        } else if (this.allFeaturesPromise) {
            //Has been called already and has not resolved yet.
            return this.allFeaturesPromise;
        }

        this.featureService.all().then(
            (response) => {
                const json = response["data"];
                if (angular.isDefined(json.data)) {
                    this.featurePermissions = {};
                    this.consentSettings = {};
                    this.securitySettings = {};
                    angular.forEach(json.data, (value) => {
                        if (consentNames.indexOf(value.attributes.index) > -1) {
                            this.consentSettings[value.attributes.index] =
                                new Feature(
                                    this.$q,
                                    this.featureService,
                                    value
                                );
                        } else if (
                            security.indexOf(value.attributes.index) > -1
                        ) {
                            this.securitySettings[value.attributes.index] =
                                new Feature(
                                    this.$q,
                                    this.featureService,
                                    value
                                );
                        } else {
                            this.featurePermissions[value.attributes.index] =
                                new Feature(
                                    this.$q,
                                    this.featureService,
                                    value
                                );
                        }
                    });
                    const permissionsFeature =
                        this.featurePermissions["PERMISSIONS"];
                    if (permissionsFeature?.attributes?.extra) {
                        const extra = permissionsFeature.attributes.extra;
                        this.levels.forEach((level: PermissionLevel) => {
                            const custom: any = extra.find(
                                (e: any): boolean => e.id === level.permissionid
                            );
                            if (custom) {
                                level.permissionname = custom.name;
                                level.permissionexplanation =
                                    custom.description;
                            }
                        });
                    }
                    deferred.resolve(this.featurePermissions);
                } else {
                    deferred.reject(json.errors);
                }
            },
            (error) => deferred.reject(error)
        );
        this.allFeaturesPromise = deferred.promise;
        return this.allFeaturesPromise;
    }

    grabFeaturePermissions() {
        if (this.featurePermissions !== null) {
            return this.featurePermissions;
        } else {
            this.getFeaturePermissions().then(() => {
                return this.featurePermissions;
            });
        }
    }

    updateFeaturePermission(newFeaturePermission) {
        angular.forEach(this.featurePermissions, (featurePermission) => {
            if (
                featurePermission.attributes.index ===
                newFeaturePermission.index
            ) {
                featurePermission.attributes = newFeaturePermission;
            }
        });
        this.updateValueInSettings(newFeaturePermission);
    }

    hasFeature(featureIndex, userPermission?): Promise<any> {
        if (!this.permissionid && !this.permissionid)
            return this.$q.resolve(false);
        const deferred = this.$q.defer();
        let permissionIdTmp = userPermission
            ? userPermission
            : this.permissionid;
        this.getFeaturePermissions().then(() => {
            deferred.resolve(
                this.featurePermissions[featureIndex].hasPermission(
                    permissionIdTmp
                )
            );
        });
        return deferred.promise;
    }

    updateValueInSettings(feature): boolean {
        if (this.consentSettings[feature.index]) {
            this.consentSettings[feature.index].attributes = feature;
        }
        if (this.securitySettings[feature.index]) {
            this.securitySettings[feature.index].attributes = feature;
        }
        return true;
    }

    featureGrants(featureIndex: string): boolean {
        const features: any = this.featurePermissions;
        const permissionId: any = this.permissionid;
        const feature: any = features?.[featureIndex];
        return !!(
            permissionId && feature && feature.hasPermission(permissionId)
        );
    }

    getFeature(featureIndex): Promise<any> {
        const deferred = this.$q.defer();
        this.getFeaturePermissions().then(() => {
            deferred.resolve(
                new Feature(
                    this.$q,
                    this.featureService,
                    this.featurePermissions[featureIndex]
                )
            );
        });
        return deferred.promise;
    }

    hasPermission(
        action: string,
        subjectPermission?: any,
        userPermission?: any
    ): boolean {
        let permissionid = userPermission ? userPermission : this.permissionid;
        permissionid = parseInt(permissionid);
        if (!permissionid) {
            return false;
        } else if (
            [
                "INVOICES",
                "CANCEL_CAMPAIGN",
                "DATA_RECORD",
                "CHANGE_PLAN",
                "2FA",
                "BILLING",
                "EDIT_CAMPAIGN_NAME",
                "PRIVACY_COMPLIANCE",
            ].indexOf(action) > -1 &&
            this.billingRight
        ) {
            return true;
        } else if (action == "DELETE_USER") {
            if (permissionid == 8 || permissionid == 9) {
                return subjectPermission != 8;
            } else if (permissionid == 2) {
                return subjectPermission != 2 && subjectPermission != 8;
            } else if (permissionid == 10) {
                return (
                    subjectPermission != 2 &&
                    subjectPermission != 8 &&
                    subjectPermission != 9
                );
            } else if (permissionid == 3) {
                return (
                    !subjectPermission ||
                    subjectPermission == 3 ||
                    subjectPermission == 4 ||
                    subjectPermission == 5 ||
                    subjectPermission == 6
                );
            }
            return false;
        } else if ((this.features as readonly string[]).indexOf(action) > -1) {
            if (
                (permissionid == 5 || permissionid == 6) &&
                (this.canvasserNotAllowedFeatures as readonly string[]).indexOf(
                    action
                ) > -1
            ) {
                return false;
            }
            if (!this.featureCalled) {
                this.featureCalled = true;
                this.hasFeature(action).then((response) => {
                    return response;
                });
            } else {
                if (this.featurePermissions) {
                    if (!this.featurePermissions[action]) {
                        this.resultFeaturePermission = false;
                    } else {
                        this.resultFeaturePermission =
                            this.featurePermissions[action].hasPermission(
                                permissionid
                            );
                    }
                }
            }

            return this.resultFeaturePermission;
        } else {
            let permissions = {
                FEATURE_SETTINGS: [2, 3, 8, 9, 10],
                PRIVACY_COMPLIANCE: [2, 3, 8, 9, 10],
                ORGANISATION_INVITE: [8, 9],
                INVOICES: [2],
                CANCEL_CAMPAIGN: [2],
                DELETE_FILTER: [2, 3, 4, 8, 9, 10],
                SAVE_FILTER: [2, 3, 4, 8, 9, 10],
                DASHBOARD_PAGES: [2, 3, 4, 5, 6, 8, 9, 10],
                CAMPAIGN_SETTINGS: [2, 3, 8, 9, 10],
                LEADER_PAGES: [8, 9, 10],
                LEADER_SETTINGS: [8, 9],
                CAMPAIGN_PAGE: [2, 3, 4, 8, 9, 10],
                EDIT_CANVASS: [2, 3, 8, 9, 10],
                TEAM_PAGE: [2, 3, 4, 8, 9, 10],
                TEAM_VIEW: [2, 3, 8, 9, 10],
                RESULTS_PAGE: [2, 3, 8, 9, 10],
                EMAIL_BLAST_PAGE: [2, 3, 8, 9, 10],
                SIDEBAR: [2, 3, 4, 5, 6, 8, 9, 10],
                LEAVE_CAMPAIGN: [3, 4, 5, 6, 7],
                IMPORT: [2, 3, 8, 9, 10],
                INTEGRATION: [2, 3, 8, 9, 10],
                CREATE_GROUP: [2, 3, 4, 8, 9, 10],
                CREATE_TEAM: [2, 3, 8, 9, 10],
                EDIT_GROUP: [2, 3, 4, 8, 9, 10],
                LOGS: [2, 3, 8, 9, 10],
                DELETE_GROUP: [2, 3, 8, 9, 10],
                CUSTOM_FIELDS: [2, 3, 8, 9, 10],
                SELECT_ALL: [2, 3, 4, 8, 9, 10],
                EDIT_HOUSE: [2, 3, 8, 9, 10],
                EDIT_LOCATION: [2, 3, 8, 9, 10],
                EDIT_HOUSEOCCUPANT: [2, 3, 8, 9, 10],
                EDIT_TALKING_POINT: [2, 3, 8, 9, 10],
                TALKING_POINT: [2, 3, 4, 5, 6, 8, 9, 10],
                NATIONBUILDER: [2, 3, 8, 9, 10],
                EXPORT: [2, 3, 8, 9, 10],
                BULK_ACTION: [2, 3, 8, 9, 10],
                SINGLE_SELECT: [2, 3, 4, 8, 9, 10],
                ANONYMIZE: [2, 3, 8, 9, 10],
                DATA_RECORD: [2, 8],
                CREATE_PUBLIC_DASHBOARD: [2, 3, 4, 8, 9, 10],
                EDIT_DASHBOARD: [2, 3, 8, 9, 10],
                EDIT_WIDGET: [2, 3, 8, 9, 10],
                CHANGE_PLAN: [2],
                CREATE_DIVISION: [2, 3, 8, 9],
                LEADER_ANALYTICS: [2, 3, 8, 9],
                LEADER_IMPORT: [2, 3, 8, 9],
                LEADER_DASHBOARD: [2, 3, 8, 9, 10],
                LEADER_CUSTOMISE: [2, 3, 8, 9],
                LEADER_USER: [2, 3, 8, 9, 10],
                LEADER_ORGANIZE: [2, 3, 8, 9],
                LEADER_LEADERBOARD: [2, 3, 8, 9, 10],
                "2FA": [2, 8],
                DUPLICATES: [2, 3, 4, 8, 9, 10],
                NOT_CANVASSER: [2, 3, 4, 8, 9, 10],
                CANVASSER: [5, 6],
                EDIT_VOTER_NAME: [2, 3, 8, 9, 10],
                AUTO_GENERATE_VOTER_NAME: [2, 3, 8, 9, 10],
                INTERCOM: [2, 3, 8, 9, 10],
                LICENSES: [2, 3, 8, 9, 10],
                BILLING: [2, 8],
                CREATE_ACCOUNT: [2],
                GETTING_STARTED: [2, 3],
                CLEAR_SAMPLE_DATA: [2, 3, 8, 9],
                PROSPECTOR: [2, 3, 8, 9, 10],
            };
            if (permissions.hasOwnProperty(action)) {
                return permissions[action].indexOf(permissionid) > -1;
            }
            return false;
        }
    }

    getAdminFeatures(campaignid: string, oid?: number): Promise<any> {
        const deferred = this.$q.defer();
        let url, params;

        if (oid) {
            url = this.__env.apiUrl() + "/organisation/feature/";
            params = { oid: oid };
        } else {
            url = this.__env.apiUrl() + "/feature/";
            params = { campaignid: campaignid };
        }

        this.$http({
            url: url,
            method: "GET",
            params: { params: params },
        }).then((response: object) => {
            const json = response["data"];
            if (json.data) {
                let features = [];
                angular.forEach(json.data, (feature: object[]) => {
                    features.push(
                        new Feature(this.$q, this.featureService, feature)
                    );
                });
                deferred.resolve(features);
            } else {
                deferred.reject();
            }
        });

        return deferred.promise;
    }

    getPermission(permissionid): null | any {
        let permission = null;
        angular.forEach(this.levels, (level) => {
            if (level.permissionid == permissionid) {
                permission = level;
            }
        });
        return permission;
    }

    setConsentSettings(consent) {
        let newValue = consent.attributes.owner;

        consent.attributes.organisationowner = newValue;
        consent.attributes.organisationleader = newValue;
        consent.attributes.divisionmanager = newValue;
        consent.attributes.campaignmanager = newValue;
        consent.attributes.campaignstaffer = newValue;
        consent.attributes.leadcanvasser = newValue;
        consent.attributes.canvasser = newValue;

        consent.update();
    }

    consentSettingsDisabled(item, consentSettings, activeCampaign): boolean {
        if (
            item &&
            item.attributes &&
            item.attributes.index !== "SIGNATURE" &&
            activeCampaign.campaignregion == "eu"
        ) {
            return consentSettings["SIGNATURE"].attributes.owner === 0;
        } else {
            return false;
        }
    }
}

angular.module("permission", []).service("PermissionCheck", PermissionCheck);
